// Transient notifications (bottom-right stack). Pushed from anywhere —
// quick add-to-cart, wishlist, undoable removals — and rendered once by
// ToastHost.vue in App.vue. Each toast auto-dismisses; hovering pauses it.
// `action` renders a link (`to`) or a button (`onClick`, e.g. Undo).
import { defineStore } from 'pinia';

export interface ToastAction {
  label: string;
  to?: string;
  onClick?: () => void;
}

export interface Toast {
  id: number;
  title: string;
  body?: string;
  image?: string | null;
  /** Unset reads as success (the historic default of every push). */
  tone?: 'default' | 'success' | 'error';
  action?: ToastAction;
}

const DEFAULT_MS = 3800;
/** Undo toasts stay up longer so the reader can reach the button. */
export const UNDO_MS = 6000;
/** Visible stack cap; pushing a fourth drops the oldest. */
export const MAX_TOASTS = 3;

let seq = 0;
// Timers live outside reactive state: they are bookkeeping, not UI.
const timers = new Map<number, { handle: number; ms: number }>();

export const useToastStore = defineStore('toast', {
  state: () => ({ items: [] as Toast[] }),
  actions: {
    push(toast: Omit<Toast, 'id'>, ms = DEFAULT_MS): number {
      const id = ++seq;
      this.items.push({ id, ...toast });
      while (this.items.length > MAX_TOASTS) this.dismiss(this.items[0].id);
      timers.set(id, { handle: window.setTimeout(() => this.dismiss(id), ms), ms });
      return id;
    },
    dismiss(id: number) {
      window.clearTimeout(timers.get(id)?.handle);
      timers.delete(id);
      this.items = this.items.filter((t) => t.id !== id);
    },
    /** Hover/focus hold: stop the clock. */
    pause(id: number) {
      const timer = timers.get(id);
      if (timer) window.clearTimeout(timer.handle);
    },
    /** Re-arm with the full duration so the reader gets a fresh window. */
    resume(id: number) {
      const timer = timers.get(id);
      if (timer) timer.handle = window.setTimeout(() => this.dismiss(id), timer.ms);
    },
  },
});
